import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { TrendingUp, ArrowRight, BadgeIndianRupee, BarChart3, Users } from 'lucide-react';
import { trackPageView } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const t = {
  tag: 'DEALER EARNINGS',
  title: 'Jaaniye Aapki',
  titleAccent: 'Kamai Kitni Hogi',
  subtitle: 'Dekhiye Lion Cement se aap kitna zyada kama sakte hain competitive brands ki tulna mein. Koi seema nahi — jitna bechoge, utna kamaoge.',
  bagsPerDay: 'Roz Kitne Bags Bechte Hain',
  workingDays: 'Mahine Mein Kitne Din Kaam',
  calculate: 'Kamai Dekhein',
  daily: 'Rozana', monthly: 'Mahina', yearly: 'Saalana',
  lionCement: 'Lion Cement', compBrands: 'Anya Brands',
  yourExtra: 'Aapki Extra Kamai',
  marginPerBag: 'Ek Bag Par Margin',
  earningsScale: 'Kamai Ka Scale — Koi Seema Nahi',
  bagsDay: 'Bags/Din',
  monthlyEarning: 'Maheena Kamai',
  disclaimer: 'Kamai ka anumaan hai aur location, volume aur market conditions ke anusar badal sakta hai.',
  becomeDealer: 'Dealer Baniye',
  applyNow: 'Abhi Apply Karein',
};

const DealerEarningsPage = () => {
  const [bags, setBags] = useState(100);
  const [workingDays, setWorkingDays] = useState(26);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('monthly');
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  useEffect(() => { trackPageView('/dealer-earnings', 'Dealer Earnings Calculator'); }, []);

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/dealer-earnings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bags_per_day: bags, working_days: workingDays }),
      });
      setResults(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const getValues = () => {
    if (!results) return { lion: 0, comp: 0, extra: 0 };
    if (view === 'daily') return { lion: results.lion_daily, comp: results.comp_daily, extra: results.extra_daily };
    if (view === 'yearly') return { lion: results.lion_yearly, comp: results.comp_yearly, extra: results.extra_yearly };
    return { lion: results.lion_monthly, comp: results.comp_monthly, extra: results.extra_monthly };
  };

  const v = getValues();
  const maxVal = Math.max(v.lion, 1);

  return (
    <div className="min-h-screen bg-void" data-testid="dealer-earnings-page">
      <SEOHead
        title="Dealer Earnings Calculator"
        description="Calculate your potential monthly and yearly earnings as a Lion Cement dealer. Compare margins with competitor brands."
        path="/dealer-earnings"
      />
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <motion.div className="absolute top-1/4 right-0 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(245,166,35,0.12) 0%, transparent 60%)' }}
          animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 8, repeat: Infinity }} />
        <div className="container mx-auto px-4 md:px-8 max-w-6xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <span className="text-avi-gold font-mono text-sm mb-4 block">{t.tag}</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              {t.title}<br /><span className="text-avi-gold">{t.titleAccent}</span>
            </h1>
            <p className="text-gray-400 text-lg mt-4 max-w-xl">{t.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Calculator */}
      <section className="pb-16">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Input */}
            <div className="lg:col-span-2">
              <div className="bg-surface border border-white/5 rounded-2xl p-6 space-y-6 sticky top-28">
                <div>
                  <label className="font-display text-sm font-medium text-white mb-2 block">{t.bagsPerDay}</label>
                  <input type="number" value={bags} onChange={e => setBags(Math.max(1, parseInt(e.target.value) || 0))}
                    data-testid="earnings-bags"
                    className="w-full bg-void border border-white/10 focus:border-avi-gold rounded-xl px-4 py-3.5 text-white text-2xl font-display font-bold outline-none text-center" />
                  <div className="flex gap-2 mt-2">
                    {[50, 100, 200, 500, 1000].map(n => (
                      <button key={n} onClick={() => setBags(n)}
                        className={`flex-1 text-xs py-2 rounded-lg font-display transition-all ${bags === n ? 'bg-avi-gold/20 text-avi-gold border border-avi-gold/30' : 'bg-void border border-white/10 text-gray-500'}`}>{n}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="font-display text-sm font-medium text-white mb-2 block">{t.workingDays}: <span className="text-avi-gold">{workingDays}</span></label>
                  <input type="range" min={20} max={30} value={workingDays} onChange={e => setWorkingDays(Number(e.target.value))}
                    className="w-full h-2 bg-void rounded-lg appearance-none cursor-pointer accent-avi-gold" />
                </div>
                {/* Margin comparison */}
                <div className="bg-void/50 border border-white/5 rounded-xl p-4 space-y-3">
                  <p className="text-gray-500 text-xs font-mono uppercase">{t.marginPerBag}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-coral font-display font-bold text-lg">Rs.30-45</span>
                    <span className="text-coral text-xs">{t.lionCement}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 font-display font-bold text-lg">Rs.10-15</span>
                    <span className="text-gray-500 text-xs">{t.compBrands}</span>
                  </div>
                </div>
                <button onClick={handleCalculate} disabled={loading} data-testid="earnings-calculate-btn"
                  className="w-full flex items-center justify-center gap-2 bg-avi-gold hover:bg-avi-gold/80 text-void font-display font-bold py-4 rounded-full transition-all disabled:opacity-50">
                  {loading ? <div className="w-5 h-5 border-2 border-void border-t-transparent rounded-full animate-spin" /> : <><TrendingUp className="w-5 h-5" /> {t.calculate}</>}
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="lg:col-span-3">
              <AnimatePresence mode="wait">
                {results ? (
                  <motion.div key="results" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="earnings-results">
                    {/* Period toggle */}
                    <div className="flex gap-2 bg-surface border border-white/5 rounded-full p-1">
                      {['daily', 'monthly', 'yearly'].map(p => (
                        <button key={p} onClick={() => setView(p)}
                          className={`flex-1 py-2.5 rounded-full text-sm font-display font-medium transition-all ${view === p ? 'bg-avi-gold text-void' : 'text-gray-400 hover:text-white'}`}>
                          {t[p]}
                        </button>
                      ))}
                    </div>

                    {/* Big comparison bars */}
                    <div className="bg-surface border border-white/5 rounded-2xl p-6 space-y-6">
                      {/* Lion */}
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-coral font-display font-semibold text-sm">{t.lionCement}</span>
                          <motion.span key={`lion-${view}`} initial={{ scale: 0.5 }} animate={{ scale: 1 }}
                            className="text-coral font-display font-black text-2xl">Rs.{v.lion.toLocaleString()}</motion.span>
                        </div>
                        <div className="h-8 bg-void rounded-xl overflow-hidden">
                          <motion.div className="h-full rounded-xl bg-gradient-to-r from-coral to-avi-gold"
                            initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 1 }} />
                        </div>
                      </div>
                      {/* Competitor */}
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-500 font-display font-semibold text-sm">{t.compBrands}</span>
                          <motion.span key={`comp-${view}`} initial={{ scale: 0.5 }} animate={{ scale: 1 }}
                            className="text-gray-400 font-display font-black text-2xl">Rs.{v.comp.toLocaleString()}</motion.span>
                        </div>
                        <div className="h-8 bg-void rounded-xl overflow-hidden">
                          <motion.div className="h-full rounded-xl bg-gray-600"
                            initial={{ width: 0 }} animate={{ width: `${(v.comp / maxVal) * 100}%` }} transition={{ duration: 1 }} />
                        </div>
                      </div>
                      {/* Extra */}
                      <div className="border-t border-white/5 pt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-mint font-display font-semibold">{t.yourExtra}</span>
                          <motion.span key={`extra-${view}`} initial={{ scale: 0.5 }} animate={{ scale: 1 }}
                            className="text-mint font-display font-black text-3xl">+Rs.{v.extra.toLocaleString()}</motion.span>
                        </div>
                      </div>
                    </div>

                    {/* Earnings Scale Chart */}
                    <div ref={ref} className="bg-surface border border-white/5 rounded-2xl p-6">
                      <h3 className="font-display text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-avi-gold" /> {t.earningsScale}
                      </h3>
                      <div className="space-y-4">
                        {results.tiers.map((tier, i) => (
                          <motion.div key={tier.bags} initial={{ opacity: 0, x: -20 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ delay: i * 0.1 }}
                            className={`flex items-center gap-4 ${tier.bags === bags ? 'ring-2 ring-avi-gold/30 rounded-xl' : ''} p-3 rounded-xl`}>
                            <div className="w-16 text-right">
                              <span className="text-white font-display font-bold text-sm">{tier.bags}</span>
                              <span className="text-gray-600 text-xs block">{t.bagsDay}</span>
                            </div>
                            <div className="flex-1 space-y-1">
                              <div className="h-4 bg-void rounded-full overflow-hidden">
                                <motion.div className="h-full rounded-full bg-gradient-to-r from-coral to-avi-gold"
                                  initial={{ width: 0 }} animate={inView ? { width: `${(tier.lion_monthly / results.tiers[results.tiers.length - 1].lion_monthly) * 100}%` } : {}}
                                  transition={{ duration: 1, delay: i * 0.1 }} />
                              </div>
                              <div className="h-4 bg-void rounded-full overflow-hidden">
                                <motion.div className="h-full rounded-full bg-gray-600"
                                  initial={{ width: 0 }} animate={inView ? { width: `${(tier.comp_monthly / results.tiers[results.tiers.length - 1].lion_monthly) * 100}%` } : {}}
                                  transition={{ duration: 1, delay: i * 0.1 }} />
                              </div>
                            </div>
                            <div className="w-28 text-right">
                              <span className="text-coral font-display font-bold text-sm">Rs.{tier.lion_monthly.toLocaleString()}</span>
                              <span className="text-gray-600 text-xs block">vs Rs.{tier.comp_monthly.toLocaleString()}</span>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                      <div className="flex items-center gap-6 mt-4 pt-4 border-t border-white/5 text-xs text-gray-500">
                        <div className="flex items-center gap-2"><div className="w-6 h-2 rounded bg-gradient-to-r from-coral to-avi-gold" />{t.lionCement}</div>
                        <div className="flex items-center gap-2"><div className="w-6 h-2 rounded bg-gray-600" />{t.compBrands}</div>
                      </div>
                    </div>

                    {/* CTAs */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link to="/dealer-form" data-testid="earnings-cta-apply"
                        className="flex-1 flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium py-4 rounded-full transition-all">
                        <Users className="w-5 h-5" /> {t.applyNow} <ArrowRight className="w-5 h-5" />
                      </Link>
                      <Link to="/distributors"
                        className="flex-1 flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-white font-display font-medium py-4 rounded-full transition-all">
                        {t.becomeDealer}
                      </Link>
                    </div>

                    <p className="text-gray-600 text-xs text-center">{t.disclaimer}</p>
                  </motion.div>
                ) : (
                  <div className="bg-surface border border-white/5 rounded-2xl p-12 text-center" data-testid="earnings-empty">
                    <div className="w-20 h-20 rounded-2xl bg-avi-gold/10 flex items-center justify-center mx-auto mb-6">
                      <BadgeIndianRupee className="w-10 h-10 text-avi-gold" />
                    </div>
                    <h3 className="font-display text-xl font-bold text-white mb-2">Aap kitna kama sakte hain?</h3>
                    <p className="text-gray-400 text-sm">Roz bechne wale bags dalein aur Lion Cement se apni kamai dekhein.</p>
                  </div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DealerEarningsPage;
